import { completePendingLogout } from "../../lib/logoutSession"
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { saveCachedSession, loadCachedSession, clearCachedSession, hasExplicitLogout, markExplicitLogout, clearExplicitLogout, authSessionGeneration } from "../../utils/authStorage"

// Seed the initial state from a locally cached session (if any) so that a
// fresh app launch with no network connectivity yet still shows the
// previously logged-in user instead of bouncing straight to the login
// screen. checkAuthStatus() will still run and confirm/refute this against
// the server as soon as a connection is available.
const cachedUser = loadCachedSession()

// Initial state
const initialState = {
  user: cachedUser,
  isAuthenticated: Boolean(cachedUser),
  loading: true,
  error: null,
  loginAttempts: 0,
  lastLoginAttempt: null,
  // True when the last session check/refresh couldn't reach the server
  // (offline/slow connection) but we're keeping a previously cached
  // session alive rather than logging the user out.
  authCheckOffline: Boolean(cachedUser),
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8005/api"

// A request that can't reach the server at all (offline, timed out, DNS
// failure, etc.) is a NETWORK error — it tells us nothing about whether the
// session/credentials are valid. Only a response that actually came back
// from the server (401/403/etc.) is an AUTH error. Callers use this to
// decide whether to keep a cached session alive or clear it.
const ERROR_TYPES = {
  NETWORK: "network",
  AUTH: "auth",
}

const DEFAULT_TIMEOUT_MS = 15000

/**
 * fetch() wrapped with an explicit timeout so a slow/stalled connection
 * fails fast with a clear "timed out" error instead of spinning forever.
 */
async function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    clearTimeout(timeoutId)
  }
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false
}

/**
 * Normalizes a thrown fetch error (offline, timeout, DNS failure, CORS,
 * etc.) into a NETWORK-typed rejection with a clear, user-facing message.
 */
function networkErrorPayload(error) {
  if (isOffline()) {
    return { type: ERROR_TYPES.NETWORK, message: "You're offline. Connect to the internet and try again." }
  }
  if (error?.name === "AbortError") {
    return { type: ERROR_TYPES.NETWORK, message: "This is taking longer than usual. Check your connection and try again." }
  }
  return { type: ERROR_TYPES.NETWORK, message: "Network error. Check your connection and try again." }
}

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const loginUser = createAsyncThunk("auth/loginUser", async (credentials, { rejectWithValue }) => {
  // Login can never succeed without reaching the server, so fail fast and
  // clearly instead of letting the user submit and wait on a doomed request.
  if (isOffline()) {
    return rejectWithValue({
      type: ERROR_TYPES.NETWORK,
      message: "You're offline. Connect to the internet to sign in.",
    })
  }

  if (!await completePendingLogout()) {
    return rejectWithValue({ type: ERROR_TYPES.NETWORK, message: "Unable to finish signing out of the previous session. Check your connection and try signing in again." })
  }
  const generation = authSessionGeneration()
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies
      body: JSON.stringify(credentials),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Invalid email or password" })
    }

    const data = await response.json()
    if (authSessionGeneration() !== generation) return rejectWithValue({ type: ERROR_TYPES.AUTH, message: "Sign-in cancelled because you signed out." })
    return {
      user: data.data.user,
    }
  } catch (error) {
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const registerUser = createAsyncThunk("auth/registerUser", async (userData, { rejectWithValue }) => {
  // Registration can never succeed without reaching the server, so fail
  // fast instead of letting the user submit into a doomed request.
  if (isOffline()) {
    return rejectWithValue({
      type: ERROR_TYPES.NETWORK,
      message: "You're offline. Connect to the internet to create an account.",
    })
  }

  if (!await completePendingLogout()) {
    return rejectWithValue({ type: ERROR_TYPES.NETWORK, message: "Unable to finish signing out of the previous session. Check your connection and try signing in again." })
  }
  const generation = authSessionGeneration()
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(userData),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Registration failed" })
    }

    const data = await response.json()
    if (authSessionGeneration() !== generation) return rejectWithValue({ type: ERROR_TYPES.AUTH, message: "Sign-in cancelled because you signed out." })
    return {
      user: data.data.user,
    }
  } catch (error) {
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  await completePendingLogout()
  return null
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const checkAuthStatus = createAsyncThunk("auth/checkAuthStatus", async (_, { rejectWithValue }) => {
  if (hasExplicitLogout()) return rejectWithValue({ type: ERROR_TYPES.AUTH, message: "Signed out" })
  const generation = authSessionGeneration()
  console.log("[v0] checkAuthStatus started")
  // Restoring a session shouldn't require a round-trip if we can't make
  // one — if we're offline, keep whatever session state is already cached
  // in Redux instead of bouncing an already-logged-in user to the login
  // screen. The request will run again automatically once we're back online.
  if (isOffline()) {
    console.log("[v0] checkAuthStatus: offline, bailing")
    return rejectWithValue({ type: ERROR_TYPES.NETWORK, message: "Offline — using cached session" })
  }

  try {
    console.log("[v0] checkAuthStatus: fetching", `${API_BASE_URL}/auth/verify`)
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/verify`, {
      method: "GET",
      credentials: "include",
    })
    console.log("[v0] checkAuthStatus: response", response.status, response.ok)

    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    if (!response.ok) {
      // A response actually came back from the server, so this is a real
      // auth rejection (expired/invalid session) — not a connectivity issue.
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: "Not authenticated" })
    }

    const data = await response.json()

    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    return {
      user: data.data.user,
    }
  } catch (error) {
    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    console.log("[v0] checkAuthStatus: caught error", error?.name, error?.message)
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const getMe = createAsyncThunk("auth/getMe", async (_, { rejectWithValue }) => {
  if (hasExplicitLogout()) return rejectWithValue({ type: ERROR_TYPES.AUTH, message: "Signed out" })
  const generation = authSessionGeneration()
  if (isOffline()) {
    return rejectWithValue({ type: ERROR_TYPES.NETWORK, message: "Offline — using cached profile" })
  }

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/me`, {
      method: "GET",
      credentials: "include",
    })

    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Failed to fetch user profile" })
    }

    const data = await response.json()
    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    return {
      user: data.data.user,
    }
  } catch (error) {
    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const updateProfile = createAsyncThunk("auth/updateProfile", async (profileData, { rejectWithValue }) => {
  if (hasExplicitLogout()) return rejectWithValue({ type: "stale", message: "Signed out" })
  const generation = authSessionGeneration()
  if (isOffline()) {
    return rejectWithValue({
      type: ERROR_TYPES.NETWORK,
      message: "You're offline. Connect to the internet to update your profile.",
    })
  }

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(profileData),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Profile update failed" })
    }

    const data = await response.json()
    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    return {
      user: data.data.user,
    }
  } catch (error) {
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const changePassword = createAsyncThunk("auth/changePassword", async (passwordData, { rejectWithValue }) => {
  if (isOffline()) {
    return rejectWithValue({
      type: ERROR_TYPES.NETWORK,
      message: "You're offline. Connect to the internet to change your password.",
    })
  }

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/change-password`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(passwordData),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Password change failed" })
    }

    const data = await response.json()
    return data.message || "Password changed successfully"
  } catch (error) {
    return rejectWithValue(networkErrorPayload(error))
  }
})


/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const forgotPassword = createAsyncThunk("auth/forgotPassword", async (email, { rejectWithValue }) => {
  if (isOffline()) {
    return rejectWithValue({
      type: ERROR_TYPES.NETWORK,
      message: "You're offline. Connect to the internet to request a password reset.",
    })
  }

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ email }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Failed to send reset email" })
    }

    const data = await response.json()
    return data.message || "Password reset email sent successfully"
  } catch (error) {
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const resetPassword = createAsyncThunk("auth/resetPassword", async (resetData, { rejectWithValue }) => {
  if (isOffline()) {
    return rejectWithValue({
      type: ERROR_TYPES.NETWORK,
      message: "You're offline. Connect to the internet to reset your password.",
    })
  }

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(resetData),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Password reset failed" })
    }

    const data = await response.json()
    return data.message || "Password reset successfully"
  } catch (error) {
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const verifyEmail = createAsyncThunk("auth/verifyEmail", async (verificationData, { rejectWithValue }) => {
  if (isOffline()) {
    return rejectWithValue({
      type: ERROR_TYPES.NETWORK,
      message: "You're offline. Connect to the internet to verify your email.",
    })
  }

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/verify-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(verificationData),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Email verification failed" })
    }

    const data = await response.json()
    return {
      user: data.data.user,
      message: data.message || "Email verified successfully",
    }
  } catch (error) {
    return rejectWithValue(networkErrorPayload(error))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const refreshToken = createAsyncThunk("auth/refreshToken", async (_, { rejectWithValue }) => {
  if (hasExplicitLogout()) return rejectWithValue({ type: ERROR_TYPES.AUTH, message: "Signed out" })
  const generation = authSessionGeneration()
  if (isOffline()) {
    return rejectWithValue({ type: ERROR_TYPES.NETWORK, message: "Offline — using cached session" })
  }

  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/refresh-token`, {
      method: "POST",
      credentials: "include",
    })

    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      return rejectWithValue({ type: ERROR_TYPES.AUTH, message: error.message || "Token refresh failed" })
    }

    const data = await response.json()
    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    return {
      user: data.data.user,
    }
  } catch (error) {
    if (hasExplicitLogout() || generation !== authSessionGeneration()) return rejectWithValue({ type: "stale", message: "Session changed" })
    return rejectWithValue(networkErrorPayload(error))
  }
})

// Auth slice
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0
      state.lastLoginAttempt = null
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload }
    },
    clearAuth: (state) => {
      state.user = null
      state.isAuthenticated = false
      state.error = null
      clearCachedSession()
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        clearExplicitLogout()
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
        state.loginAttempts = 0
        state.lastLoginAttempt = null
        state.authCheckOffline = false
        saveCachedSession(action.payload.user)
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || action.payload || "Login failed"
        // A failed network attempt (offline/timeout) isn't a wrong password —
        // don't count it toward the lockout-style attempt counter.
        if (action.payload?.type !== "network") {
          state.loginAttempts += 1
          state.lastLoginAttempt = new Date().toISOString()
        }
      })

      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        clearExplicitLogout()
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
        state.authCheckOffline = false
        saveCachedSession(action.payload.user)
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        markExplicitLogout()
        state.loginAttempts = 0
        state.lastLoginAttempt = null
        state.user = null
        state.isAuthenticated = false
        state.loading = false
        state.error = null
        state.authCheckOffline = false
      })
      .addCase(checkAuthStatus.pending, (state) => {
        state.loading = true
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        if (hasExplicitLogout()) { state.loading = false; return }
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
        state.authCheckOffline = false
        saveCachedSession(action.payload.user)
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        if (action.payload?.type === "stale") { state.loading = false; return }
        state.loading = false
        state.error = null
        if (action.payload?.type === "network") {
          // Couldn't reach the server (offline/slow connection) — keep
          // whatever session we already had cached instead of logging the
          // user out. It'll be re-verified automatically once we're online.
          state.authCheckOffline = true
          return
        }
        // The server actually responded and said the session is invalid —
        // this is a real logout, not a connectivity blip.
        state.authCheckOffline = false
        state.user = null
        state.isAuthenticated = false
        clearCachedSession()
      })

      .addCase(getMe.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getMe.fulfilled, (state, action) => {
        if (hasExplicitLogout()) { state.loading = false; return }
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
        saveCachedSession(action.payload.user)
      })
      .addCase(getMe.rejected, (state, action) => {
        if (action.payload?.type === "stale") { state.loading = false; return }
        state.loading = false
        state.error = action.payload
      })

      .addCase(updateProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (hasExplicitLogout()) { state.loading = false; return }
        state.loading = false
        state.user = action.payload.user
        state.error = null
        saveCachedSession(action.payload.user)
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(changePassword.pending, (state) => {
        state.loading = true
        state.error = null
        state.passwordChangeSuccess = false
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false
        state.error = null
        state.passwordChangeSuccess = true
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.passwordChangeSuccess = false
      })
    

      .addCase(forgotPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(resetPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(verifyEmail.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        if (hasExplicitLogout()) { state.loading = false; return }
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
        saveCachedSession(action.payload.user)
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(refreshToken.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        if (hasExplicitLogout()) { state.loading = false; return }
        state.loading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
        state.authCheckOffline = false
        saveCachedSession(action.payload.user)
      })
      .addCase(refreshToken.rejected, (state, action) => {
        if (action.payload?.type === "stale") { state.loading = false; return }
        state.loading = false
        state.error = null
        if (action.payload?.type === "network") {
          // Couldn't reach the server — keep the existing session cached
          // rather than logging the user out over a connectivity blip.
          state.authCheckOffline = true
          return
        }
        // The server actually rejected the refresh (expired/invalid) —
        // this is a real logout.
        state.authCheckOffline = false
        state.user = null
        state.isAuthenticated = false
        clearCachedSession()
      })
  },
})

export const { clearError, resetLoginAttempts, updateUser, clearAuth } = authSlice.actions
export default authSlice.reducer
